import { useState, useEffect } from 'react';
import { adminApi, AdminAnalytics } from '../services/api';
import { 
    BarChart3, 
    Link as LinkIcon, 
    Users, 
    Mail, 
    Calendar, 
    CheckCircle,
    Clock,
    AlertCircle
} from 'lucide-react';

export function AdminPage() {
    const [analytics, setAnalytics] = useState<AdminAnalytics | null>(null);
    const [loading, setLoading] = useState(true);
    const [error, setError] = useState<string | null>(null);

    useEffect(() => {
        loadAnalytics();
    }, []);

    const loadAnalytics = async () => {
        try {
            setLoading(true);
            setError(null);
            const data = await adminApi.getAnalytics();
            setAnalytics(data);
        } catch (err: any) {
            setError(err.message || 'Failed to load analytics');
        } finally {
            setLoading(false);
        }
    };

    if (loading) {
        return (
            <div className="page container">
                <div style={{ display: 'flex', alignItems: 'center', justifyContent: 'center', minHeight: '400px' }}>
                    <div className="spinner" />
                </div>
            </div>
        );
    }

    if (error) {
        return (
            <div className="page container">
                <div className="integrations-header">
                    <div className="integrations-header-icon">
                        <BarChart3 size={28} />
                    </div>
                    <div>
                        <h1>Admin Analytics</h1>
                        <p className="integrations-subtitle">
                            System-wide analytics and usage statistics
                        </p>
                    </div>
                </div>
                <div className="card" style={{ padding: 'var(--space-6)', textAlign: 'center' }}>
                    <AlertCircle size={48} style={{ color: 'var(--color-danger)', marginBottom: 'var(--space-4)' }} />
                    <h3 style={{ marginBottom: 'var(--space-2)' }}>Access Denied</h3>
                    <p style={{ color: 'var(--color-gray-500)' }}>{error}</p>
                </div>
            </div>
        );
    }

    if (!analytics) return null;

    return (
        <div className="page container">
            {/* Header */}
            <div className="integrations-header">
                <div className="integrations-header-icon">
                    <BarChart3 size={28} />
                </div>
                <div>
                    <h1>Admin Analytics</h1>
                    <p className="integrations-subtitle">
                        System-wide analytics and usage statistics
                    </p>
                </div>
            </div>

            {/* Overview Stats */}
            <div style={{ 
                display: 'grid', 
                gridTemplateColumns: 'repeat(auto-fit, minmax(200px, 1fr))', 
                gap: 'var(--space-4)',
                marginBottom: 'var(--space-6)'
            }}>
                <StatCard
                    icon={<Users size={24} />}
                    label="Unique Users"
                    value={analytics.unique_users}
                    color="var(--color-primary)"
                />
                <StatCard
                    icon={<LinkIcon size={24} />}
                    label="Scheduling Links"
                    value={analytics.scheduling_links.total}
                    subValue={`${analytics.scheduling_links.active} active`}
                    color="var(--color-success)"
                />
                <StatCard
                    icon={<Calendar size={24} />}
                    label="Total Proposals"
                    value={analytics.proposals.total}
                    subValue={`${analytics.proposals.by_status?.confirmed || 0} confirmed`}
                    color="var(--color-warning)"
                />
                <StatCard
                    icon={<Mail size={24} />}
                    label="Emails Sent"
                    value={analytics.emails.sent}
                    subValue={analytics.emails.failed > 0 ? `${analytics.emails.failed} failed` : undefined}
                    color="var(--color-info)"
                />
            </div>

            {/* Integrations Section */}
            <h2 style={{ fontSize: 'var(--font-size-lg)', marginBottom: 'var(--space-4)' }}>Integrations</h2>
            <div style={{ 
                display: 'grid', 
                gridTemplateColumns: 'repeat(auto-fit, minmax(250px, 1fr))', 
                gap: 'var(--space-4)',
                marginBottom: 'var(--space-6)'
            }}>
                <div className="card" style={{ padding: 'var(--space-4)' }}>
                    <div style={{ display: 'flex', alignItems: 'center', gap: 'var(--space-3)', marginBottom: 'var(--space-3)' }}>
                        <div style={{ 
                            width: '40px', 
                            height: '40px', 
                            borderRadius: 'var(--radius-md)', 
                            background: '#0078d4',
                            display: 'flex',
                            alignItems: 'center',
                            justifyContent: 'center',
                            color: 'white'
                        }}>
                            <svg viewBox="0 0 24 24" width="20" height="20" fill="currentColor">
                                <path d="M11.5 3v8.5H3V3h8.5zm0 18H3v-8.5h8.5V21zM21 3v8.5h-8.5V3H21zm0 18h-8.5v-8.5H21V21z"/>
                            </svg>
                        </div>
                        <div>
                            <h3 style={{ fontSize: 'var(--font-size-base)', fontWeight: 600 }}>Microsoft</h3>
                            <p style={{ fontSize: 'var(--font-size-sm)', color: 'var(--color-gray-500)' }}>Calendar & Teams</p>
                        </div>
                    </div>
                    <div style={{ 
                        fontSize: 'var(--font-size-2xl)', 
                        fontWeight: 700,
                        color: 'var(--color-text)'
                    }}>
                        {analytics.microsoft_integrations}
                    </div>
                    <p style={{ fontSize: 'var(--font-size-sm)', color: 'var(--color-gray-500)' }}>
                        users connected
                    </p>
                </div>

                <div className="card" style={{ padding: 'var(--space-4)' }}>
                    <div style={{ display: 'flex', alignItems: 'center', gap: 'var(--space-3)', marginBottom: 'var(--space-3)' }}>
                        <div style={{ 
                            width: '40px', 
                            height: '40px', 
                            borderRadius: 'var(--radius-md)', 
                            background: '#ff7a59',
                            display: 'flex',
                            alignItems: 'center',
                            justifyContent: 'center',
                            color: 'white'
                        }}>
                            <svg viewBox="0 0 24 24" width="20" height="20" fill="currentColor">
                                <path d="M17.5 8.5a2 2 0 0 0-2-2v-2a1 1 0 0 0-2 0v2a2 2 0 0 0 0 4 2 2 0 0 0 0 4v2a1 1 0 0 0 2 0v-2a2 2 0 0 0 2-2 2 2 0 0 0-2-2 2 2 0 0 0 2-2z"/>
                            </svg>
                        </div>
                        <div>
                            <h3 style={{ fontSize: 'var(--font-size-base)', fontWeight: 600 }}>HubSpot</h3>
                            <p style={{ fontSize: 'var(--font-size-sm)', color: 'var(--color-gray-500)' }}>CRM Integration</p>
                        </div>
                    </div>
                    <div style={{ 
                        fontSize: 'var(--font-size-2xl)', 
                        fontWeight: 700,
                        color: 'var(--color-text)'
                    }}>
                        {analytics.hubspot_integrations}
                    </div>
                    <p style={{ fontSize: 'var(--font-size-sm)', color: 'var(--color-gray-500)' }}>
                        users connected ({analytics.hubspot_enabled_links} links enabled)
                    </p>
                </div>
            </div>

            {/* Recent Activity Section */}
            <h2 style={{ fontSize: 'var(--font-size-lg)', marginBottom: 'var(--space-4)' }}>Last 30 Days</h2>
            <div style={{ 
                display: 'grid', 
                gridTemplateColumns: 'repeat(auto-fit, minmax(180px, 1fr))', 
                gap: 'var(--space-4)',
                marginBottom: 'var(--space-6)'
            }}>
                <ActivityCard
                    icon={<LinkIcon size={18} />}
                    label="New Links"
                    value={analytics.recent_activity.new_links_30d}
                />
                <ActivityCard
                    icon={<Clock size={18} />}
                    label="New Proposals"
                    value={analytics.recent_activity.new_proposals_30d}
                />
                <ActivityCard
                    icon={<CheckCircle size={18} />}
                    label="Confirmed"
                    value={analytics.recent_activity.confirmed_proposals_30d}
                />
                <ActivityCard
                    icon={<Calendar size={18} />}
                    label="New Events"
                    value={analytics.recent_activity.new_events_30d}
                />
            </div>

            {/* Detailed Breakdowns */}
            <div style={{ 
                display: 'grid', 
                gridTemplateColumns: 'repeat(auto-fit, minmax(300px, 1fr))', 
                gap: 'var(--space-4)'
            }}>
                {/* Proposals by Status */}
                <div className="card" style={{ padding: 'var(--space-4)' }}>
                    <h3 style={{ fontSize: 'var(--font-size-base)', fontWeight: 600, marginBottom: 'var(--space-4)' }}>
                        Proposals by Status
                    </h3>
                    <div style={{ display: 'flex', flexDirection: 'column', gap: 'var(--space-3)' }}>
                        <StatusRow label="Pending" value={analytics.proposals.by_status?.pending || 0} color="var(--color-warning)" />
                        <StatusRow label="Confirmed" value={analytics.proposals.by_status?.confirmed || 0} color="var(--color-success)" />
                        <StatusRow label="Declined" value={analytics.proposals.by_status?.declined || 0} color="var(--color-danger)" />
                        <StatusRow label="Expired" value={analytics.proposals.by_status?.expired || 0} color="var(--color-gray-400)" />
                    </div>
                </div>

                {/* Events by Status */}
                <div className="card" style={{ padding: 'var(--space-4)' }}>
                    <h3 style={{ fontSize: 'var(--font-size-base)', fontWeight: 600, marginBottom: 'var(--space-4)' }}>
                        Meeting Polls by Status
                    </h3>
                    <div style={{ display: 'flex', flexDirection: 'column', gap: 'var(--space-3)' }}>
                        <StatusRow label="Draft" value={analytics.events.by_status?.draft || 0} color="var(--color-gray-400)" />
                        <StatusRow label="Sent" value={analytics.events.by_status?.sent || 0} color="var(--color-primary)" />
                        <StatusRow label="Closed" value={analytics.events.by_status?.closed || 0} color="var(--color-success)" />
                        <StatusRow label="Canceled" value={analytics.events.by_status?.canceled || 0} color="var(--color-danger)" />
                    </div>
                </div>

                {/* Scheduling Links Summary */}
                <div className="card" style={{ padding: 'var(--space-4)' }}>
                    <h3 style={{ fontSize: 'var(--font-size-base)', fontWeight: 600, marginBottom: 'var(--space-4)' }}>
                        Scheduling Links
                    </h3>
                    <div style={{ display: 'flex', flexDirection: 'column', gap: 'var(--space-3)' }}>
                        <StatusRow label="Active" value={analytics.scheduling_links.active} color="var(--color-success)" />
                        <StatusRow label="Inactive" value={analytics.scheduling_links.inactive} color="var(--color-gray-400)" />
                        <div style={{ 
                            marginTop: 'var(--space-2)',
                            paddingTop: 'var(--space-2)',
                            borderTop: '1px solid var(--color-border)',
                            display: 'flex',
                            justifyContent: 'space-between',
                            fontWeight: 600
                        }}>
                            <span>Total</span>
                            <span>{analytics.scheduling_links.total}</span>
                        </div>
                    </div>
                </div>
            </div>
        </div>
    );
}

// Stat Card Component
function StatCard({ 
    icon, 
    label, 
    value, 
    subValue,
    color 
}: { 
    icon: React.ReactNode; 
    label: string; 
    value: number;
    subValue?: string;
    color: string;
}) {
    return (
        <div className="card" style={{ padding: 'var(--space-4)' }}>
            <div style={{ display: 'flex', alignItems: 'center', gap: 'var(--space-3)', marginBottom: 'var(--space-3)' }}>
                <div style={{ color }}>{icon}</div>
                <span style={{ fontSize: 'var(--font-size-sm)', color: 'var(--color-gray-500)' }}>{label}</span>
            </div>
            <div style={{ fontSize: 'var(--font-size-2xl)', fontWeight: 700 }}>{value.toLocaleString()}</div>
            {subValue && (
                <p style={{ fontSize: 'var(--font-size-sm)', color: 'var(--color-gray-500)', marginTop: 'var(--space-1)' }}>
                    {subValue}
                </p>
            )}
        </div>
    );
}

// Activity Card Component
function ActivityCard({ 
    icon, 
    label, 
    value 
}: { 
    icon: React.ReactNode; 
    label: string; 
    value: number;
}) {
    return (
        <div className="card" style={{ padding: 'var(--space-4)', display: 'flex', alignItems: 'center', gap: 'var(--space-3)' }}>
            <div style={{ 
                width: '36px', 
                height: '36px', 
                borderRadius: 'var(--radius-md)', 
                background: 'var(--color-primary-light)',
                display: 'flex',
                alignItems: 'center',
                justifyContent: 'center',
                color: 'var(--color-primary)'
            }}>
                {icon}
            </div>
            <div>
                <div style={{ fontSize: 'var(--font-size-lg)', fontWeight: 700 }}>{value.toLocaleString()}</div>
                <div style={{ fontSize: 'var(--font-size-sm)', color: 'var(--color-gray-500)' }}>{label}</div>
            </div>
        </div>
    );
}

// Status Row Component
function StatusRow({ 
    label, 
    value, 
    color 
}: { 
    label: string; 
    value: number; 
    color: string;
}) {
    return (
        <div style={{ display: 'flex', alignItems: 'center', justifyContent: 'space-between' }}>
            <div style={{ display: 'flex', alignItems: 'center', gap: 'var(--space-2)' }}>
                <div style={{ 
                    width: '10px', 
                    height: '10px', 
                    borderRadius: '50%', 
                    background: color 
                }} />
                <span style={{ fontSize: 'var(--font-size-sm)' }}>{label}</span>
            </div>
            <span style={{ fontWeight: 600 }}>{value.toLocaleString()}</span>
        </div>
    );
}
